import { Input, Select } from '@/components/ui/FormControls';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Copy,
  ExternalLink,
  Loader2,
  FileText,
  Download,
  Share2,
  RefreshCw,
  MessageCircle,
} from 'lucide-react';
import {
  saleWhatsAppMessage,
  saleWhatsAppUrl,
  whatsappRecipient,
  saleMoney,
} from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import { fetchSaleShare } from '@/lib/salesOverview';
import { canShareReceipt } from '@/lib/saleReceiptShare';
import { useSaleReceiptShare } from '@/hooks/useSaleReceiptShare';
import WorkModalShell from '@/components/common/WorkModalShell';
import WhatsAppMessagePreview from './WhatsAppMessagePreview';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.1-1.1l-.4-.2-2.9.8.8-2.8-.3-.4A8 8 0 1 1 12 20Zm4.4-5.8c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.2l-.7.8c-.1.2-.3.2-.5.1a6.6 6.6 0 0 1-3.1-2.7c-.2-.3.2-.4.5-.9.1-.2 0-.4 0-.5l-.7-1.6c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.7.7-1 1.4-.7 2.4.4 1.8 2.2 3.8 4.5 4.8 1.5.6 2.3.7 3.1.3.6-.3 1-1.2 1.1-1.6.1-.2 0-.3-.5-.4Z" />
    </svg>
  );
}
export default function SaleWhatsAppAction({
  saleId,
  label = false,
}: {
  saleId: string;
  label?: boolean;
}) {
  const { can, profile, access } = useAuth();
  const navigate = useNavigate();
  const tabs = useRef<Partial<Record<'message' | 'pdf', HTMLButtonElement | null>>>({});
  const [open, setOpen] = useState(false),
    [address, setAddress] = useState(true),
    [country, setCountry] = useState<'IN' | 'international'>('IN'),
    [mode, setMode] = useState<'message' | 'pdf'>('message'),
    [raw, setRaw] = useState(false),
    [opening, setOpening] = useState(false),
    [sharing, setSharing] = useState(false),
    [verifiedUrl, setVerifiedUrl] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['sale_whatsapp', saleId, profile?.id, access?.access_version],
    queryFn: () => fetchSaleShare(saleId),
    enabled: open && can('sales.whatsapp'),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const payload = query.data;
  const receipt = useSaleReceiptShare({
    saleId,
    payload,
    includeAddress: address,
    active: open,
    onChanged: async () => {
      await query.refetch();
      toast.info(
        'Sale details updated. Review the refreshed details before preparing the receipt.',
      );
    },
  });
  useEffect(() => {
    if (payload) {
      setAddress(payload.settings.include_address);
      setCountry(payload.settings.default_country);
    }
  }, [payload]);
  useEffect(() => setVerifiedUrl(null), [payload, country, address, open]);
  const busy = opening || receipt.pending || sharing;
  const available = !!payload && !query.error && !query.isFetching;
  const recipient = whatsappRecipient(payload?.customer_mobile ?? '', country);
  const message = payload ? saleWhatsAppMessage(payload, { address }) : '';
  const mobileBrowser =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const whatsappTarget = mobileBrowser ? 'universal' : 'web';
  const fileReady = available && !!receipt.file && !!receipt.url && !receipt.expired;
  const fileSharing = receipt.file ? canShareReceipt(receipt.file) : false;
  async function openChat(compact = false) {
    if (busy || !available || !recipient.digits || !payload) return;
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setOpening(true);
    setVerifiedUrl(null);
    try {
      const current = await fetchSaleShare(saleId);
      if (JSON.stringify(current) !== JSON.stringify(payload)) {
        tab?.close();
        await query.refetch();
        toast.info('The saved sale changed. Review the refreshed message before opening WhatsApp.');
        return;
      }
      const url = saleWhatsAppUrl(
        recipient.digits,
        compact ? saleWhatsAppMessage(current, { address: false, compact: true }) : message,
        whatsappTarget,
      );
      if (url.length > 7000) {
        tab?.close();
        toast.info('Copy the full message, then use the compact summary for this long order.');
        return;
      }
      if (tab) tab.location.replace(url);
      else {
        setVerifiedUrl(url);
        toast.info('Your browser blocked the tab. Use Continue to WhatsApp below.');
      }
    } catch (e) {
      tab?.close();
      toast.error(
        e instanceof Error ? e.message : 'WhatsApp could not be opened. Try copying the message.',
      );
    } finally {
      setOpening(false);
    }
  }
  async function sharePdf() {
    if (!fileReady || !receipt.file || busy || !can('sales.whatsapp')) return;
    if (!receipt.preparedAt || Date.now() - receipt.preparedAt >= 60000) {
      void receipt.prepare();
      toast.info('Refreshing the receipt. Tap Share PDF when it is ready.');
      return;
    }
    if (!fileSharing) {
      toast.info('Download the PDF, then attach it in the customer chat.');
      return;
    }
    // No asynchronous work before share: file creation and authorization checks happened in Prepare.
    const file = receipt.file;
    setSharing(true);
    try {
      await navigator.share({ files: [file] });
      toast.info('Receipt handed to the share menu. Confirm the recipient in WhatsApp.');
    } catch (e) {
      if ((e as { name?: string }).name !== 'AbortError')
        toast.error('File sharing was unavailable. Download the PDF and attach it in WhatsApp.');
    } finally {
      setSharing(false);
    }
  }
  if (!can('sales.whatsapp')) return null;
  const button =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:opacity-50';
  const compact =
    !!recipient.digits && saleWhatsAppUrl(recipient.digits, message, whatsappTarget).length > 7000;
  return (
    <>
      <button
        type="button"
        aria-label="WhatsApp customer"
        title="WhatsApp customer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMode('message');
          setRaw(false);
          setOpen(true);
        }}
        className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl border border-emerald-600/30 px-3 text-emerald-600 hover:bg-emerald-500/10"
      >
        <WhatsAppIcon />
        {label && <span className="text-sm font-semibold">WhatsApp</span>}
      </button>
      {open && (
        <WorkModalShell
          open
          onClose={() => setOpen(false)}
          busy={opening || sharing}
          title="Share sale with customer"
          wide
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border pb-3">
            <div>
              <h2 className="text-lg font-semibold">Share with customer</h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Send an order summary or share the receipt as a PDF.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close share preview"
              disabled={opening || sharing}
              onClick={() => setOpen(false)}
              className="min-h-11 min-w-11 rounded-lg hover:bg-muted"
            >
              <X className="mx-auto h-5 w-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
            {query.isLoading ? (
              <p role="status" className="py-8 text-center text-sm">
                Preparing sale details…
              </p>
            ) : query.error ? (
              <div role="alert" className="rounded-xl bg-destructive/10 p-4 text-sm">
                <p>{query.error.message}</p>
                <button
                  type="button"
                  className="mt-2 min-h-11 underline"
                  onClick={() => void query.refetch()}
                >
                  Retry
                </button>
              </div>
            ) : (
              payload && (
                <>
                  <div className="rounded-xl border border-border bg-muted/20 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Customer
                    </p>
                    <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                      <p className="min-w-0 break-words font-semibold">
                        {payload.customer_name || 'Customer'}
                      </p>
                      <span className="rounded-lg bg-background px-2.5 py-1.5 text-sm tabular-nums">
                        {recipient.digits
                          ? `+${recipient.digits}`
                          : payload.customer_mobile || 'No mobile number'}
                      </span>
                    </div>
                    {payload.contact_source === 'customer' && (
                      <p className="mt-2 text-xs text-amber-600">
                        Using the linked customer's current phone. Verify the recipient before
                        sharing.
                      </p>
                    )}
                  </div>
                  <div
                    role="tablist"
                    aria-label="Share format"
                    className="grid grid-cols-2 gap-1 rounded-xl border border-border bg-muted/30 p-1"
                  >
                    {(['message', 'pdf'] as const).map((tab) => (
                      <button
                        key={tab}
                        id={`sale-share-${saleId}-${tab}`}
                        aria-controls={`sale-share-${saleId}-panel`}
                        role="tab"
                        aria-selected={mode === tab}
                        ref={(element) => {
                          tabs.current[tab] = element;
                        }}
                        tabIndex={mode === tab ? 0 : -1}
                        disabled={busy}
                        onClick={() => setMode(tab)}
                        onKeyDown={(event) => {
                          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key))
                            return;
                          event.preventDefault();
                          const next =
                            event.key === 'Home'
                              ? 'message'
                              : event.key === 'End'
                                ? 'pdf'
                                : mode === 'message'
                                  ? 'pdf'
                                  : 'message';
                          setMode(next);
                          tabs.current[next]?.focus();
                        }}
                        className={`${button} ${mode === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        {tab === 'message' ? (
                          <MessageCircle className="h-4 w-4" />
                        ) : (
                          <FileText className="h-4 w-4" />
                        )}
                        {tab === 'message' ? 'Message' : 'Receipt PDF'}
                      </button>
                    ))}
                  </div>
                  <fieldset
                    disabled={busy}
                    className="grid items-end gap-3 rounded-xl border border-border p-3 sm:grid-cols-2"
                  >
                    <label className="block text-xs font-medium text-muted-foreground">
                      Phone format
                      <Select
                        value={country}
                        onChange={(e) => setCountry(e.target.value as typeof country)}
                        className="mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground"
                        helpLabel="Phone format"
                      >
                        <option value="IN">India (+91)</option>
                        <option value="international">International (+country code)</option>
                      </Select>
                    </label>
                    {payload.customer_address && (
                      <label className="flex min-h-11 items-center gap-2 text-sm">
                        <Input
                          type="checkbox"
                          className="h-4 w-4 accent-emerald-600"
                          checked={address}
                          onChange={(e) => setAddress(e.target.checked)}
                          helpLabel="Include delivery address"
                        />
                        Include delivery address
                      </label>
                    )}
                  </fieldset>
                  {recipient.error && (
                    <div
                      role="alert"
                      className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
                    >
                      <p className="font-semibold">
                        {payload.customer_mobile?.trim()
                          ? 'Check the customer phone number'
                          : 'Customer phone number is missing'}
                      </p>
                      <p>
                        {payload.customer_mobile?.trim()
                          ? recipient.error
                          : 'Add a phone number to open WhatsApp for this sale.'}
                      </p>
                      {can('sales.edit') && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setOpen(false);
                            navigate(`/sales/${saleId}/edit`);
                          }}
                          className="min-h-11 rounded-lg border border-border px-3 font-medium hover:bg-accent"
                        >
                          {payload.customer_mobile?.trim()
                            ? 'Edit phone number'
                            : 'Add phone number'}
                        </button>
                      )}
                      {!can('sales.edit') && <p>Ask an authorized user to add a phone number.</p>}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setOpen(false)}
                        className="ml-2 min-h-11 px-3 underline"
                      >
                        Close
                      </button>
                    </div>
                  )}
                  <section
                    role="tabpanel"
                    tabIndex={0}
                    id={`sale-share-${saleId}-panel`}
                    aria-labelledby={`sale-share-${saleId}-${mode}`}
                  >
                    {mode === 'message' ? (
                      <>
                        <WhatsAppMessagePreview message={message} raw={raw} />
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span>Bold headings and spacing are included in the copied message.</span>
                          <button
                            type="button"
                            onClick={() => setRaw((v) => !v)}
                            className="min-h-11 text-primary"
                          >
                            {raw ? 'Formatted preview' : 'Show copyable text'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-border bg-muted/20 p-4 sm:p-5">
                          <div className="flex items-start gap-3">
                            <div className="rounded-xl bg-emerald-500/10 p-3 text-emerald-600">
                              <FileText className="h-7 w-7" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold">
                                Order receipt · {payload.sale_number}
                              </h3>
                              <p className="mt-1 text-sm text-muted-foreground">
                                Items, customer details, payment summary and current status.
                              </p>
                              <p className="mt-2 text-lg font-bold">
                                {saleMoney(payload.total_amount)}
                              </p>
                            </div>
                          </div>
                          <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                            {receipt.pending ? (
                              <p role="status" className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Preparing and verifying your receipt…
                              </p>
                            ) : receipt.file ? (
                              <>
                                <p className="break-all font-medium text-foreground">
                                  {receipt.file.name}
                                </p>
                                <p className="mt-1">
                                  {Math.max(1, Math.round(receipt.file.size / 1024))} KB · Prepared{' '}
                                  {new Date(receipt.preparedAt!).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </>
                            ) : (
                              <p>Prepare the PDF to use the latest saved sale details.</p>
                            )}
                          </div>
                        </div>
                        {receipt.error && (
                          <p
                            role="alert"
                            className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive"
                          >
                            {receipt.error}
                          </p>
                        )}
                        {receipt.expired && (
                          <p role="status" className="text-sm text-amber-600">
                            Refresh the PDF before sharing to check for updated sale details.
                          </p>
                        )}
                        {receipt.url && !receipt.expired && (
                          <a
                            href={receipt.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${button} w-full border border-border`}
                          >
                            <ExternalLink className="h-4 w-4" />
                            Preview PDF
                          </a>
                        )}
                        <div className="rounded-xl border border-emerald-600/20 bg-emerald-500/5 p-3 text-sm leading-relaxed">
                          <p className="font-medium">
                            Choose WhatsApp, then select {payload.customer_name || 'the customer'}.
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            The share menu chooses the app and recipient. If file sharing is
                            unavailable, download the receipt and attach it in the chat.
                          </p>
                        </div>
                      </div>
                    )}
                  </section>
                </>
              )
            )}
          </div>
          <div className="shrink-0 space-y-2 border-t border-border pt-3">
            {mode === 'message' ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!available || busy}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(message);
                      toast.success('Formatted message copied');
                    } catch {
                      setRaw(true);
                      toast.info('Select the copyable text above to copy it.');
                    }
                  }}
                  className={`${button} border border-border px-2 sm:px-4`}
                >
                  <Copy className="h-4 w-4" />
                  Copy message
                </button>
                <button
                  type="button"
                  disabled={!available || !!recipient.error || busy}
                  onClick={() => void openChat(compact)}
                  className={`${button} bg-emerald-600 px-2 text-white hover:bg-emerald-700 sm:px-4`}
                >
                  {opening ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {compact ? 'Open summary' : 'Open WhatsApp'}
                </button>
              </div>
            ) : (
              <>
                {!receipt.file || receipt.expired ? (
                  <button
                    type="button"
                    disabled={!available || busy}
                    onClick={() => void receipt.prepare()}
                    className={`${button} w-full bg-emerald-600 text-white`}
                  >
                    {receipt.pending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {receipt.pending
                      ? 'Preparing PDF…'
                      : receipt.expired
                        ? 'Refresh PDF'
                        : 'Prepare receipt PDF'}
                  </button>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {fileReady ? (
                      <a
                        href={receipt.url}
                        download={receipt.file.name}
                        className={`${button} border border-border px-2`}
                        onClick={(e) => {
                          if (busy) e.preventDefault();
                          else if (
                            !receipt.preparedAt ||
                            Date.now() - receipt.preparedAt >= 60000
                          ) {
                            e.preventDefault();
                            void receipt.prepare();
                            toast.info('Refreshing the receipt. Download it when ready.');
                          }
                        }}
                        aria-disabled={busy}
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </a>
                    ) : (
                      <button disabled className={`${button} border border-border`}>
                        Download PDF
                      </button>
                    )}
                    {fileSharing ? (
                      <button
                        type="button"
                        disabled={!fileReady || busy}
                        onClick={() => void sharePdf()}
                        className={`${button} bg-emerald-600 px-2 text-white`}
                      >
                        <Share2 className="h-4 w-4" />
                        {sharing ? 'Opening…' : 'Share PDF'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!available || !!recipient.error || busy}
                        onClick={() => void openChat(true)}
                        className={`${button} border border-emerald-600/30 px-2 text-emerald-600`}
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open chat to attach
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
            {verifiedUrl && (
              <a
                href={verifiedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 items-center justify-center text-sm text-primary underline"
                onClick={() => setVerifiedUrl(null)}
              >
                Continue to WhatsApp
              </a>
            )}
            <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
              {mode === 'message'
                ? 'Review the recipient and press Send in WhatsApp.'
                : 'The PDF stays on your device until you choose to share it.'}
            </p>
          </div>
        </WorkModalShell>
      )}
    </>
  );
}
